import { ASSET_TYPE, FWKIcons, IAsset } from '@desp-aas/desp-ui-fwk';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import AssetCardNew from '../../../components/AssetCardNew/AssetCardNew';
import AssetCardNewLoading from '../../../components/AssetCardNew/AssetCardNewLoading';
import AssetCardNewDetailed from '../../../components/AssetCardNew/AssetCardNewDetailed';
import AssetCardNewDetailedLoading from '../../../components/AssetCardNew/AssetCardNewDetailedLoading';
import './CatalogResults.css';

type IProps = {
    title: string
    assets: IAsset[] | undefined
    loadingAssets: boolean
    asset_type: ASSET_TYPE
    missingFields: boolean
    loadingCount?: number
    /** force a fixed 4-per-row grid that spans the full width */
    fourPerRow?: boolean
    /** detailed mode: horizontal row cards, 2 per row */
    detailed?: boolean
    eyebrow?: string
    /** total number of matching assets (shown under the title) */
    count?: number
    /** offset of the first shown asset (for the range indicator) */
    offset?: number
    previous?: () => any
    next?: () => any
}

export function CatalogResults(props: IProps) {
    const loadingCount = props.loadingCount ?? 8;

    // Commit the range/progress only once the data has finished loading,
    // so the bar doesn't jump on the (earlier) offset change.
    const [meta, setMeta] = useState<{ from: number; to: number; progress: number; count: number }>();
    useEffect(() => {
        if (props.loadingAssets || props.missingFields) return;
        const shown = props.assets?.length ?? 0;
        if (props.count === undefined || shown === 0) { setMeta(undefined); return; }
        const offset = props.offset ?? 0;
        const to = offset + shown;
        setMeta({
            from: offset + 1,
            to,
            count: props.count,
            progress: props.count ? Math.min(100, (to / props.count) * 100) : 0,
        });
    }, [props.loadingAssets, props.missingFields, props.count, props.offset, props.assets]);
    const gridClass = classNames('cat-results-grid', {
        'cat-results-grid--row4': props.fourPerRow && !props.detailed,
        'cat-results-grid--detailed': props.detailed,
    });

    return (
        <section className="cat-results">
            <header className="cat-results-head">
                <h2 className="cat-results-title">
                    <span className="cat-results-eyebrow-row">
                        <span className="cat-results-eyebrow">{props.eyebrow ?? 'Catalog'}</span>
                        <span className="cat-results-line" />
                    </span>
                    <span className="cat-results-main-row">
                        <span className="cat-results-main">{props.title}</span>
                        {!props.missingFields && (
                            meta ? (
                                <span className={classNames('cat-results-count', { loading: props.loadingAssets })}>
                                    <span className="cat-results-range">{meta.from}–{meta.to}</span>
                                    <span className="cat-results-sep" />
                                    <span className="cat-results-total">{meta.count}</span>
                                    <span className="cat-results-results">results</span>
                                </span>
                            ) : props.loadingAssets ? (
                                <span className="cat-results-count cat-results-count--skeleton loading">
                                    <span className="cat-results-range" />
                                    <span className="cat-results-sep" />
                                    <span className="cat-results-total" />
                                    <span className="cat-results-results" />
                                </span>
                            ) : null
                        )}
                    </span>
                </h2>
                <div className="cat-results-nav">
                    <button
                        className={classNames('cat-results-nav-btn', { disabled: !props.previous || props.loadingAssets })}
                        onClick={props.previous}
                    >
                        {FWKIcons.letterChevronLeft}
                    </button>
                    <button
                        className={classNames('cat-results-nav-btn', { disabled: !props.next || props.loadingAssets })}
                        onClick={props.next}
                    >
                        {FWKIcons.letterChevronRight}
                    </button>
                </div>
            </header>

            {props.missingFields ? (
                <div className="cat-results-empty">{FWKIcons.warning} Missing required fields</div>
            ) : props.loadingAssets ? (
                <div className={gridClass}>
                    {Array.from({ length: loadingCount }).map((_, i) =>
                        props.detailed ? <AssetCardNewDetailedLoading key={i} /> : <AssetCardNewLoading key={i} />
                    )}
                </div>
            ) : !props.assets || props.assets.length === 0 ? (
                <div className="cat-results-empty">No results</div>
            ) : (
                <div className={gridClass}>
                    {props.assets.map((asset) =>
                        props.detailed
                            ? <AssetCardNewDetailed key={asset.id} asset={asset} />
                            : <AssetCardNew key={asset.id} asset={asset} />
                    )}
                </div>
            )}
        </section>
    );
}

export default CatalogResults;
